/** Mirrors api/src/model.ts and the Guardian payloads the API passes through. */

export type StepId = 'entitlement' | 'connect' | 'site' | 'upload' | 'deploy'
  | 'permissions' | 'install' | 'verify' | 'azure' | 'setup' | 'complete';

/** Azure before the setup page: the page carries the backend the azure step deployed. */
export const STEP_ORDER: StepId[] = ['entitlement', 'connect', 'site', 'upload', 'deploy', 'permissions', 'install', 'verify', 'azure', 'setup', 'complete'];

export const STEP_LABELS: Record<StepId, string> = {
  entitlement: 'Check licence in Guardian',
  connect:     'Customer Global Admin signs in',
  site:        'Prepare the SharePoint site',
  upload:      'Upload package to the app catalog',
  deploy:      'Deploy in the app catalog',
  permissions: 'Grant API permissions',
  install:     'Add the app to the site',
  verify:      'Verify the installation',
  azure:       'Azure backend',
  setup:       'Create the setup page',
  complete:    'Complete',
};

/** Steps that act in the customer tenant and therefore need the GA's tokens. */
export const NEEDS_CUSTOMER: StepId[] = ['connect', 'site', 'upload', 'deploy', 'permissions', 'install', 'verify', 'setup', 'azure'];

/** Same rule as the API's parseSiteOwner: an email or UPN ('#' for guest UPNs), at most 254 characters. */
export function isSiteOwnerLike(value: string): boolean {
  const v = value.trim();
  return v.length <= 254 && /^[A-Za-z0-9._%+'#-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(v);
}

export const SITE_OWNER_HINT = 'Leave blank to make you (the Global Admin) the owner.';

/** How the product's setup runs when the customer opens the setup link (the product reads ?mode=). */
export type SetupMode = 'auto' | 'express' | 'guided' | 'welcome';

/** The engineer's choice at the start of a deployment, in the order shown. The first is the default. */
export const SETUP_MODE_OPTIONS: Array<{ value: SetupMode; label: string }> = [
  { value: 'auto',    label: 'Set up automatically (recommended) — the site is ready when the customer first opens it' },
  { value: 'express', label: 'Run Express setup while the customer watches' },
  { value: 'guided',  label: 'Guided setup — click through each step' },
  { value: 'welcome', label: 'Let the customer choose' },
];

export type StepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export interface StepState { status: StepStatus; startedAt?: string; finishedAt?: string; message?: string; detail?: Record<string, unknown> }

export interface DeploymentSession {
  id: string; createdAt: string; updatedAt: string;
  status: 'active' | 'completed' | 'failed' | 'abandoned';
  createdBy: { objectId: string; name: string };
  lastActor?: { objectId: string; name: string };
  tenantId: string; customerName?: string;
  customerAdmin?: { username: string; signedInAt: string };
  sharePointHost?: string;
  productId: string; displayName: string; guardianAppId: string; version: string;
  siteUrl: string; siteTitle: string;
  setupMode: SetupMode;
  /** Email/UPN made a site collection admin by the site step; absent = the GA who signs in. */
  siteOwner?: string;
  catalogAppId?: string;
  azure?: AzureRun;
  steps: Record<StepId, StepState>;
}

/** The azure step's state for a product with an Azure pack (api/src/model.ts AzureRun). */
export interface AzureRun {
  mode: 'arm' | 'portal'; fallbackReason?: string;
  subscriptionId?: string; resourceGroup?: string; location?: string; deploymentName?: string;
  app?: { clientId: string; objectId: string; audience: string; resourceName?: string };
  submittedAt?: string; provisioningState?: string; healthAttempts?: number;
  outputs?: Record<string, string>;
  /** Customer Connect's temporary Azure / app-write consent, removed after the backend was done. */
  consentTrim?: { removed: boolean; trimmed: string[]; error?: string; at: string; when: string };
  consentRequestedAt?: string;
  attested?: { subscriptionId: string; resourceGroup: string; siteName: string; principalId: string };
}

export interface AzurePack {
  templateUri: string; templateSha256: string; createUiDefinitionUri?: string;
  appRegistration?: string; appRegistrationSha256?: string; allowedResourceAccess?: Record<string, string[]>;
  packageZipUri?: string; packageZipSha256?: string;
  parameters: Record<string, unknown>; outputs: string[]; healthCheckPath: string;
}

export interface AzureSubscription { subscriptionId: string; displayName: string; state: string }
export interface AzureTargets { locations: Array<{ name: string; displayName: string }>; resourceGroups: Array<{ name: string; location: string }> }

export interface ProductManifest {
  schemaVersion: 2; productId: string; displayName: string; guardianAppId: string; version: string;
  package: { blobPath: string; fileName: string; solutionId: string; tenantWideDeploy: boolean };
  site: { defaultPath: string; defaultTitle: string; setupPage: { pageName: string; webPartId: string } };
  apiPermissions: Array<{ resource: string; scope: string }>;
  azure: { required: boolean; optional?: boolean; guideUrl?: string; packBlobPath?: string;
           outputs?: Array<{ key: string; label: string; help?: string }>; healthCheckPath?: string; pack?: AzurePack };
  packageUploaded?: boolean;
}

export interface GuardianInstallation {
  tenantId: string; appId: string; State: string; CustomerName: string | null; DeploymentId: string | null;
  Version: string | null; SiteUrl: string | null; UpdatedBy: string | null; LastError: string | null; StateChangedAt: string;
  /** Release-management fields; Guardian may send camelCase or PascalCase, so read through readInstallVersions(). */
  [extra: string]: unknown;
}

export interface Me { engineer: { objectId: string; name: string; username: string }; customerAppClientId: string }
